import { Typography, Tooltip, Fade } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { UserWithMatchedSkills } from '../../model/project-builder'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { AppCheckbox } from 'src/feature/form/component/analog/AppCheckbox'
import { observer } from 'mobx-react-lite'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppFormControlWithLabel } from 'src/feature/form/component/analog/AppFormControlWithLabel'
import { ColorTokens, Palette, RadiusTokens } from 'src/feature/theme/model/Palette'
import { AppAvatar } from 'src/component/common/analog/molecule/AppAvatar'
import GroupsIcon from '@mui/icons-material/Groups'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { API_BASE_URL } from 'src/util/constant'

export type MatchedUsersListProps = {
  usersWithUsedSkills: UserWithMatchedSkills[]
  usersWithUnusedSkills: UserWithMatchedSkills[]
  projectUsers?: { userId: string }[]
  selectedUsers?: Set<string>
  onSelectUser?: (userId: string) => void
  selectedSkill?: string
}

export const MatchedUsersList = observer(({
  usersWithUsedSkills,
  usersWithUnusedSkills,
  projectUsers = [],
  selectedUsers,
  onSelectUser,
  selectedSkill,
}: MatchedUsersListProps) => {
  const { translation } = useLocalizationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder

  const projectUserIds = new Set(projectUsers.map(u => u.userId))

  const filterUsers = (users: UserWithMatchedSkills[]) => users.filter(user => !projectUserIds.has(user.userId))

  const formatRoles = (roles: string[]) => {
    if (!roles.length) {
      return ''
    }
    if (roles.length <= 2) {
      return roles.join(', ')
    }

    return `${roles.slice(0, 2).join(', ')} +${roles.length - 2}`
  }

  const getLevel = (rating: number) => {
    if (rating <= 2) {
      return {
        label: 'Beginner',
        backgroundColor: '#D97A5F',
      }
    }

    if (rating === 3) {
      return {
        label: 'Intermediate',
        backgroundColor: '#F2DF63',
      }
    }

    if (rating === 4) {
      return {
        label: 'Advanced',
        backgroundColor: '#7EA6CC',
      }
    }

    return {
      label: 'Expert',
      backgroundColor: '#9AD9D0',
    }
  }

  const renderUser = (user: UserWithMatchedSkills) => {
    const filteredSkills = user.matchedSkills.filter(skill => selectedSkill
      ? skill.projectSkills.includes(selectedSkill)
      : true)

    const primarySkill = filteredSkills[0]

    if (!primarySkill) {
      return null
    }

    return (
      <Fade in={true} timeout={500} mountOnEnter unmountOnExit>
        <AppPaper key={user.userId} sx={styles.userCard}>
          <AppBox sx={styles.userCheckboxWrapper}>
            {onSelectUser && selectedUsers && (
              <AppFormControlWithLabel
                control={
                  <AppCheckbox
                    size="small"
                    checked={selectedUsers.has(user.userId)}
                    onClick={() => onSelectUser(user.userId)}
                  />
                }
                label=""
              />
            )}
            <AppBox sx={styles.avatarWrapper}>
              <AppAvatar
                src={`${API_BASE_URL}${user.avatarUrl}`}
                outerProps={{ squareSize: 48 }}
              />
            </AppBox>
          </AppBox>

          <AppBox sx={styles.userInfo}>
            <AppBox sx={styles.userNameRow}>
              <Typography sx={styles.userName}>{user.userName}</Typography>
              {user.previouslyDeclined && (
                <Typography sx={styles.previouslyDeclined}>
                  ({projectBuilderTranslation.previouslyDeclined()})
                </Typography>
              )}
            </AppBox>
            {Boolean(user.roles?.length) && (
              <Tooltip title={user.roles.join(', ')} arrow placement="top">
                <Typography sx={styles.userRoles}>
                  {formatRoles(user.roles)}
                </Typography>
              </Tooltip>
            )}

            <AppBox>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{primarySkill.organizationSkillName}</Typography>
              {(primarySkill.isValidated ?? primarySkill.isValidated) && (
                <Tooltip
                  title={projectBuilderTranslation.validated()}
                  arrow
                  placement="top"
                >
                  <CheckCircleIcon sx={styles.validatedIcon} />
                </Tooltip>
              )}
            </AppBox>
          </AppBox>

          <AppBox sx={styles.ratingContainer}>
            <AppBox sx={styles.ratingTop}>
              <StarRoundedIcon sx={styles.starIcon} />
              <Typography sx={styles.ratingText}>
                {primarySkill.actualRating}/5
              </Typography>
            </AppBox>
            <Typography
              sx={{
                ...styles.levelBadge,
                backgroundColor: getLevel(primarySkill.actualRating).backgroundColor,
              }}
            >
              {getLevel(primarySkill.actualRating).label}
            </Typography>
          </AppBox>
        </AppPaper>
      </Fade>
    )
  }

  const used = filterUsers(usersWithUsedSkills)
  const unused = filterUsers(usersWithUnusedSkills)

  const matchedUsersCount = new Set([
    ...used.map(user => user.userId),
    ...unused.map(user => user.userId),
  ]).size

  return (
    <AppBox sx={styles.container}>
      {used.length > 0 && (
        <AppBox sx={styles.section}>
          <AppBox sx={styles.sectionHeader}>
            <AppBox sx={styles.sectionHeaderLeft}>
              <GroupsIcon sx={styles.sectionIcon} />
              <AppBox sx={styles.userTitleBlock}>
                <Typography variant='h2'>{projectBuilderTranslation.usersWithUsedSkills()}</Typography>
                <Typography variant='body1' sx={styles.sectionTitle}>{projectBuilderTranslation.recommendedText()}</Typography>
              </AppBox>
            </AppBox>
            <Typography sx={styles.memberCount}>
              {matchedUsersCount} {projectBuilderTranslation.employees()}
            </Typography>
          </AppBox>
          <AppBox sx={styles.usersList}>
            {used.map(renderUser)}
          </AppBox>
        </AppBox>
      )}

      {unused.length > 0 && (
        <AppBox sx={styles.section}>
          <AppBox sx={styles.sectionHeader}>
            <AppBox sx={styles.sectionHeaderLeft}>
              <GroupsIcon sx={styles.sectionIcon} />
              <AppBox sx={styles.userTitleBlock}>
                <Typography variant='h2'>{projectBuilderTranslation.usersWithUnusedSkills()}</Typography>
                <Typography sx={styles.sectionTitle}>{projectBuilderTranslation.unusedText()}</Typography>
              </AppBox>
            </AppBox>
            <Typography sx={styles.memberCount}>
              {matchedUsersCount} {projectBuilderTranslation.employees()}
            </Typography>
          </AppBox>
          <AppBox sx={styles.usersList}>
            {unused.map(renderUser)}
          </AppBox>
        </AppBox>
      )}
    </AppBox>
  )
})

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: 20,
    padding: '10px 15px',
    width: '100%',
    maxHeight: 'none',
    overflowY: 'scroll',
    scrollbarGutter: 'stable',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    width: '100%',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  sectionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    minWidth: 0,
    flex: 1,
  },
  sectionIcon: {
    fontSize: 50,
    color: Palette.CORAL_REEF_GREEN,
    backgroundColor: ColorTokens.primary100,
    borderRadius: '12px',
    padding: '5px',
  },
  sectionTitle: {
    color: 'text.secondary',
    marginTop: '-15px',
  },
  usersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
  },
  userCard: {
    gap: 15,
    padding: '10px 15px',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'flex-start',
      md: 'center',
    },
    backgroundColor: 'background.dark',
    width: '100%',
    minWidth: 0,
    border: '1px solid',
    borderColor: 'transparent',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease-in-out',
    '&:hover': {
      borderColor: ColorTokens.border100,
    },
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 8,
  },
  userName: {
    maxWidth: '28em',
    fontSize: 13,
    fontWeight: 600,
    color: 'text.primary',
  },
  userNameRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap',
  },
  previouslyDeclined: {
    fontSize: 11,
    fontStyle: 'italic',
    color: 'text.secondary',
  },
  userTitleBlock: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
    gap: 20,
  },
  userRoles: {
    fontSize: 12,
    color: Palette.CORAL_REEF_GREEN,
  },
  userCheckboxWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  avatarWrapper: {
    width: 50,
    height: 50,
    flexShrink: 0,
    marginLeft: '-15px',
  },
  skillName: {
    fontSize: 13,
    fontWeight: 500,
    color: 'text.secondary',
  },
  ratingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    flexShrink: 0,
    width: 132,
  },
  ratingTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    width: '100%',
  },
  starIcon: {
    fontSize: 12,
    color: ColorTokens.yellow200,
  },
  levelBadge: {
    borderRadius: RadiusTokens.pill,
    padding: '5px 10px',
    minWidth: 110,
    fontSize: 12,
    lineHeight: 1.2,
    color: 'background.default',
    textAlign: 'center',
  },
  ratingText: {
    fontSize: 12,
    color: 'text.primary',
  },
  validatedIcon: {
    fontSize: 16,
    color: 'success.main',
    marginLeft: 4,
  },
  memberCount: {
    fontSize: 14,
    fontWeight: 500,
    color: 'text.secondary',
  },
} as const